"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import BrandLogo from "@/components/tenant/BrandLogo";
import { ChevronLeft, ChevronRight, ChevronDown, Search, LogOut, Lock, MoreHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { LottiePlayer } from "@/components/ui/lottie/LottiePlayer";
import { useFocusTrap } from "@/lib/use-focus-trap";
export interface SidebarNavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  href: string;
  badge?: string | number;
  /** Optional Lottie animation src (path in /public/lottie/ or URL). Plays on hover/active. */
  lottieSrc?: string;
  /** true = tautan ke aset/URL di luar router SPA (mis. /panduan/) — dirender sebagai <a> hard-nav, bukan <Link>. */
  external?: boolean;
  /**
   * Bila diisi, klik TIDAK menavigasi — handler ini yang jalan. Dipakai item
   * "Bantuan", yang membuka panel in-app alih-alih membuang seluruh state SPA
   * demi halaman docs eksternal. `href` tetap wajib supaya tautannya bisa
   * dibuka di tab baru dan tetap punya arti bagi pembaca layar.
   */
  onSelect?: () => void;
  /** true = fitur terkunci di paket tenant — tampil dengan gembok (bukan disembunyikan). */
  locked?: boolean;
}
export interface SidebarNavSection {
  /**
   * Id stabil seksi (`adminSections[].id`). Dipakai sebagai anchor coach-mark
   * `data-tour="nav-section-<id>"`. Tidak boleh diganti label: tenant bisa
   * me-rename label lewat /admin/website → Menu, dan anchor yang terikat label
   * akan lepas begitu mereka melakukannya.
   */
  id?: string;
  label?: string;
  items: SidebarNavItem[];
}
export interface ModernSidebarProps {
  brand: {
    name: string;
    subtitle?: string;
    badgeLabel?: string;
    badgeColor?: string;
  };
  sections: SidebarNavSection[];
  user?: {
    name: string;
    initials: string;
    role?: string;
    roleColor?: string;
    avatarUrl?: string | null;
    avatarSeed?: string;
  };
  onLogout?: () => void;
  className?: string;
  /** Opt-in: label section jadi tombol lipat (dipakai admin; layout user tidak berubah) */
  collapsibleSections?: boolean;
  /** Section yang terbuka default saat belum ada preferensi tersimpan (label persis) */
  defaultOpenSections?: string[];
  /**
   * Id item yang dipromosikan jadi tab utama di bar navigasi bawah (mobile).
   * Default-nya id sisi siswa; admin WAJIB mengirim id-nya sendiri, kalau tidak
   * hanya "dashboard" + "tryout" yang cocok sehingga barnya cuma berisi 3 sel
   * selebar ~33% layar masing-masing.
   */
  primaryTabIds?: string[];
}

/**
 * Membungkus item nav: item.external → <a> (hard navigation, mis. ke aset statis
 * /panduan/ di luar router SPA); selain itu → next/link <Link>. forwardRef agar
 * MobileBottomNav yang memakai ref untuk auto-scroll tetap berfungsi.
 */
const NavLink = React.forwardRef<HTMLAnchorElement, {
  item: SidebarNavItem;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">>(function NavLink(t0, ref) {
  const $ = _c(20);
  let children;
  let item;
  let onClick;
  let rest;
  if ($[0] !== t0) {
    ({
      item,
      children,
      onClick,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = item;
    $[3] = onClick;
    $[4] = rest;
  } else {
    children = $[1];
    item = $[2];
    onClick = $[3];
    rest = $[4];
  }
  let t1;
  if ($[5] !== item.onSelect || $[6] !== onClick) {
    t1 = item.onSelect ? e => {
      onClick?.(e);
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
        return;
      }
      e.preventDefault();
      item.onSelect();
    } : onClick;
    $[5] = item.onSelect;
    $[6] = onClick;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleClick = t1;
  if (item.external) {
    let t2;
    if ($[8] !== children || $[9] !== handleClick || $[10] !== item.href || $[11] !== ref || $[12] !== rest) {
      t2 = <a ref={ref} href={item.href} onClick={handleClick} {...rest}>{children}</a>;
      $[8] = children;
      $[9] = handleClick;
      $[10] = item.href;
      $[11] = ref;
      $[12] = rest;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  let t2;
  if ($[14] !== children || $[15] !== handleClick || $[16] !== item.href || $[17] !== ref || $[18] !== rest) {
    t2 = <Link ref={ref} href={item.href} prefetch={false} onClick={handleClick} {...rest}>{children}</Link>;
    $[14] = children;
    $[15] = handleClick;
    $[16] = item.href;
    $[17] = ref;
    $[18] = rest;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
});
export function ModernSidebar(t0) {
  const $ = _c(114);
  const {
    brand,
    sections,
    user,
    onLogout,
    className,
    collapsibleSections: t1,
    defaultOpenSections,
    primaryTabIds
  } = t0;
  const collapsibleSections = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const t2 = brand.subtitle || brand.name;
  let t3;
  if ($[0] !== t2) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /\s+/g;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    t3 = t2.toLowerCase().replace(t4, "-");
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const sectionsStorageKey = `sidebar-sections-${t3}`;
  let t4;
  if ($[3] !== defaultOpenSections || $[4] !== sections) {
    t4 = () => {
      const initial = {};
      for (const s of sections) {
        if (s.label) {
          initial[s.label] = defaultOpenSections ? defaultOpenSections.includes(s.label) : true;
        }
      }
      return initial;
    };
    $[3] = defaultOpenSections;
    $[4] = sections;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [openSections, setOpenSections] = useState(t4);
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const stored = localStorage.getItem("sidebar-collapsed");
      if (stored === "true") {
        const frame = requestAnimationFrame(() => setIsCollapsed(true));
        return () => cancelAnimationFrame(frame);
      }
    };
    t6 = [];
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== sectionsStorageKey) {
    t7 = label => {
      setOpenSections(prev => {
        const next = {
          ...prev,
          [label]: !prev[label]
        };
        try {
          localStorage.setItem(sectionsStorageKey, JSON.stringify(next));
        } catch {}
        return next;
      });
    };
    $[8] = sectionsStorageKey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const toggleSection = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsCollapsed(_temp);
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const toggleCollapse = t8;
  let t9;
  bb0: {
    if (!search.trim()) {
      t9 = sections;
      break bb0;
    }
    let t10;
    if ($[11] !== search || $[12] !== sections) {
      const q = search.toLowerCase();
      t10 = sections.map(s_0 => ({
        ...s_0,
        items: s_0.items.filter(i => i.label.toLowerCase().includes(q))
      })).filter(_temp2);
      $[11] = search;
      $[12] = sections;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    t9 = t10;
  }
  const filteredSections = t9;
  let t10;
  if ($[14] !== pathname || $[15] !== sections) {
    let t11;
    if ($[17] !== pathname) {
      t11 = item => pathname === item.href || item.href !== "/" && pathname.startsWith(`${item.href}/`);
      $[17] = pathname;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    t10 = sections.flatMap(_temp3).filter(t11).sort(_temp4);
    $[14] = pathname;
    $[15] = sections;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const matches = t10;
  const activeHref = matches[0]?.href ?? "";
  let t11;
  if ($[19] !== activeHref || $[20] !== sections) {
    t11 = sections.find(s_2 => !!s_2.label && s_2.items.some(i_0 => i_0.href === activeHref))?.label ?? null;
    $[19] = activeHref;
    $[20] = sections;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const activeSectionLabel = t11;
  const [autoOpenedLabel, setAutoOpenedLabel] = useState(null);
  if (collapsibleSections && activeSectionLabel && activeSectionLabel !== autoOpenedLabel) {
    setAutoOpenedLabel(activeSectionLabel);
    setOpenSections(prev_1 => prev_1[activeSectionLabel] ? prev_1 : {
      ...prev_1,
      [activeSectionLabel]: true
    });
  }
  let t12;
  let t13;
  if ($[22] !== activeSectionLabel || $[23] !== collapsibleSections || $[24] !== sectionsStorageKey) {
    t12 = () => {
      if (!collapsibleSections) {
        return;
      }
      try {
        const stored_0 = localStorage.getItem(sectionsStorageKey);
        if (stored_0) {
          const parsed = JSON.parse(stored_0) as Record<string, boolean>;
          if (activeSectionLabel) {
            delete parsed[activeSectionLabel];
          }
          const frame_0 = requestAnimationFrame(() => setOpenSections(prev_2 => ({
            ...prev_2,
            ...parsed
          })));
          return () => cancelAnimationFrame(frame_0);
        }
      } catch {}
    };
    t13 = [collapsibleSections, sectionsStorageKey, activeSectionLabel];
    $[22] = activeSectionLabel;
    $[23] = collapsibleSections;
    $[24] = sectionsStorageKey;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  const avatarUrl = user?.avatarUrl || (user ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(user.avatarSeed || user.name || user.initials)}` : "");
  let t14;
  if ($[27] !== activeHref || $[28] !== brand.name || $[29] !== onLogout || $[30] !== primaryTabIds || $[31] !== sections) {
    t14 = <MobileBottomNav sections={sections} activeHref={activeHref} brandName={brand.name} onLogout={onLogout} primaryTabIds={primaryTabIds} />;
    $[27] = activeHref;
    $[28] = brand.name;
    $[29] = onLogout;
    $[30] = primaryTabIds;
    $[31] = sections;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = isCollapsed ? "w-[var(--sidebar-w-collapsed)]" : "w-[var(--sidebar-w)]";
  let t16;
  if ($[33] !== className || $[34] !== t15) {
    t16 = cn("bg-white hidden h-full flex-col transition-all duration-300 ease-in-out md:flex", "border-r border-[rgba(0,0,0,0.07)] shadow-[var(--shadow-sidebar)]", t15, "relative z-auto", className);
    $[33] = className;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = isCollapsed ? "flex-col items-center justify-center gap-1.5 px-1 py-2.5" : "items-center justify-between px-4 py-3.5";
  let t18;
  if ($[36] !== t17) {
    t18 = cn("flex border-b border-[rgba(0,0,0,0.06)] min-h-[60px]", t17);
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== brand.badgeColor || $[39] !== brand.badgeLabel || $[40] !== brand.name || $[41] !== brand.subtitle || $[42] !== isCollapsed) {
    t19 = !isCollapsed && <div className="flex items-center gap-2 min-w-0"><BrandLogo variant="mark" width={28} height={28} className="rounded-lg shrink-0 object-contain" priority={true} /><div className="flex flex-col min-w-0"><span className="font-bold text-[13px] text-[#09090B] leading-tight truncate tracking-[-0.01em]">{brand.name}</span>{brand.subtitle && <span className="text-[11px] text-[#A1A1AA] leading-tight truncate">{brand.subtitle}</span>}</div>{brand.badgeLabel && <span className={cn("shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-md border", brand.badgeColor ?? "bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] border-[var(--brand-primary)]/15")}>{brand.badgeLabel}</span>}</div>;
    $[38] = brand.badgeColor;
    $[39] = brand.badgeLabel;
    $[40] = brand.name;
    $[41] = brand.subtitle;
    $[42] = isCollapsed;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== isCollapsed) {
    t20 = isCollapsed && <BrandLogo variant="mark" width={32} height={32} className="rounded-xl shrink-0 object-contain shadow-sm" priority={true} />;
    $[44] = isCollapsed;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = isCollapsed ? "h-7 w-7" : "p-2.5 ml-auto";
  let t22;
  if ($[46] !== t21) {
    t22 = cn("hidden md:flex items-center justify-center shrink-0 rounded-lg hover:bg-black/[0.04] transition-colors duration-150", t21);
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  const t23 = isCollapsed ? "Lebarkan menu samping" : "Ciutkan menu samping";
  const t24 = isCollapsed ? "Lebarkan menu samping" : "Ciutkan menu samping";
  let t25;
  if ($[48] !== isCollapsed) {
    t25 = isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-gray-400" /> : <ChevronLeft className="h-3.5 w-3.5 text-gray-400" />;
    $[48] = isCollapsed;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t22 || $[51] !== t23 || $[52] !== t24 || $[53] !== t25) {
    t26 = <button onClick={toggleCollapse} className={t22} aria-label={t23} title={t24}>{t25}</button>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t18 || $[56] !== t19 || $[57] !== t20 || $[58] !== t26) {
    t27 = <div className={t18}>{t19}{t20}{t26}</div>;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== isCollapsed || $[61] !== search) {
    t28 = !isCollapsed && <div className="px-3 pt-3 pb-1"><div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A1A1AA] pointer-events-none" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari menu..." className="w-full pl-8 pr-3 py-1.5 rounded-[var(--radius-md,8px)] text-[12px] bg-[#F4F4F5] border border-transparent text-[#09090B] placeholder:text-[#A1A1AA] outline-none focus:bg-white focus:border-[rgba(0,0,0,0.10)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--brand-primary)_6%,transparent)] transition-all duration-150" /></div></div>;
    $[60] = isCollapsed;
    $[61] = search;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== activeHref || $[64] !== collapsibleSections || $[65] !== filteredSections || $[66] !== isCollapsed || $[67] !== openSections || $[68] !== search || $[69] !== toggleSection) {
    let t30;
    if ($[71] !== activeHref || $[72] !== collapsibleSections || $[73] !== isCollapsed || $[74] !== openSections || $[75] !== search || $[76] !== toggleSection) {
      t30 = (section_0, si) => {
        const isSearching = search.trim().length > 0;
        const canToggle = collapsibleSections && !isCollapsed && !!section_0.label && !isSearching;
        const isOpen = !canToggle || openSections[section_0.label] !== false;
        const containsActive = section_0.items.some(i_1 => i_1.href === activeHref);
        return <div key={si}>{section_0.label && !isCollapsed && (canToggle ? <button onClick={() => toggleSection(section_0.label)} data-tour={section_0.id ? `nav-section-${section_0.id}` : undefined} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-2 px-3 py-2 min-h-[36px] rounded-[var(--radius-md,8px)] text-[10px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA] hover:text-[#71717A] hover:bg-black/[0.02] transition-colors duration-150 cursor-pointer focus-visible:outline-2 focus-visible:outline-[var(--brand-primary)]/40"><span className="flex items-center gap-1.5 truncate">{section_0.label}{!isOpen && containsActive && <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-primary)]" aria-hidden={true} />}</span><ChevronDown className={cn("h-3 w-3 shrink-0 transition-transform duration-200", isOpen ? "rotate-0" : "-rotate-90")} /></button> : <p className="px-3 mb-1 mt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">{section_0.label}</p>)}{(isOpen || isCollapsed) && <ul className="space-y-px">{section_0.items.map(item_0 => {
              const Icon = item_0.icon;
              const isActive = item_0.href === activeHref;
              return <li key={item_0.id}><NavLink item={item_0} data-tour={`nav-item-${item_0.id}`} title={isCollapsed ? item_0.label : undefined} className={cn("relative flex items-center rounded-[var(--radius-md,8px)] text-[13px] font-medium transition-all duration-150 active:scale-[0.98] group", isCollapsed ? "justify-center p-2.5" : "gap-2.5 px-3 py-2", isActive ? "bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-fg)]" : "text-[#3F3F46] hover:bg-black/[0.03] hover:text-[#09090B]")}>{isActive && !isCollapsed && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2.5px] rounded-full bg-[var(--sidebar-active-fg)]" />}{item_0.lottieSrc ? <div className="h-[15px] w-[15px] shrink-0"><LottiePlayer src={item_0.lottieSrc} loop={isActive} autoplay={isActive} hoverPlay={!isActive} className="w-full h-full" /></div> : <Icon className={cn("h-[15px] w-[15px] shrink-0 transition-colors duration-150", isActive ? "text-[var(--sidebar-active-fg)]" : "text-[#A1A1AA] group-hover:text-[#71717A]")} />}{!isCollapsed && <><span className="flex-1 truncate">{item_0.label}</span>{item_0.locked ? <Lock className="h-3.5 w-3.5 shrink-0 text-[#A1A1AA]" aria-label="Fitur paket berbayar" /> : item_0.badge !== undefined ? <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded-md", isActive ? "bg-[color-mix(in_oklab,var(--sidebar-active-fg)_10%,transparent)] text-[var(--sidebar-active-fg)]" : "bg-[#F4F4F5] text-[#71717A]")}>{item_0.badge}</span> : null}</>}{isCollapsed && item_0.locked && <Lock className="absolute right-0.5 top-0.5 h-2.5 w-2.5 text-[#A1A1AA]" aria-label="Terkunci" />}{isCollapsed && <div className="absolute left-full ml-2.5 px-2.5 py-1.5 bg-[#0D0D0D] text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap z-50 pointer-events-none shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.15)]">{item_0.label}{item_0.badge !== undefined && <span className="ml-1.5 px-1 py-0.5 bg-white/20 rounded-full text-[10px]">{item_0.badge}</span>}<div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0D0D0D]" /></div>}</NavLink></li>;
            })}</ul>}</div>;
      };
      $[71] = activeHref;
      $[72] = collapsibleSections;
      $[73] = isCollapsed;
      $[74] = openSections;
      $[75] = search;
      $[76] = toggleSection;
      $[77] = t30;
    } else {
      t30 = $[77];
    }
    t29 = filteredSections.map(t30);
    $[63] = activeHref;
    $[64] = collapsibleSections;
    $[65] = filteredSections;
    $[66] = isCollapsed;
    $[67] = openSections;
    $[68] = search;
    $[69] = toggleSection;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[78] !== t29) {
    t30 = <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-3">{t29}</nav>;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== avatarUrl || $[81] !== isCollapsed || $[82] !== user) {
    t31 = user && <div className={cn("p-3", isCollapsed ? "flex justify-center" : "")}>{!isCollapsed ? <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-gray-50 transition-colors duration-150"><img src={avatarUrl} alt={user.name} className="w-8 h-8 rounded-full bg-white/62 object-cover ring-1 ring-black/[0.06] shrink-0" /><div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800 truncate leading-tight">{user.name}</p>{user.role && <p className={`text-xs truncate leading-tight ${user.roleColor ?? "text-gray-400"}`}>{user.role}</p>}</div><div className="w-2 h-2 bg-green-500 rounded-full shrink-0" /></div> : <div className="relative"><img src={avatarUrl} alt={user.name} className="w-9 h-9 rounded-full bg-white/62 object-cover ring-1 ring-black/[0.06]" /><div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" /></div>}</div>;
    $[80] = avatarUrl;
    $[81] = isCollapsed;
    $[82] = user;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  const t32 = isCollapsed ? "flex justify-center" : "";
  let t33;
  if ($[84] !== t32) {
    t33 = cn("px-3 pb-3", t32);
    $[84] = t32;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  const t34 = isCollapsed ? "Keluar" : undefined;
  const t35 = isCollapsed ? "p-2.5 justify-center relative" : "w-full gap-2.5 px-3 py-2.5";
  let t36;
  if ($[86] !== t35) {
    t36 = cn("flex items-center rounded-lg text-sm font-medium transition-all duration-150 group", "text-red-500 hover:bg-red-50 hover:text-red-600", t35);
    $[86] = t35;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <LogOut className="h-4 w-4 shrink-0" />;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== isCollapsed) {
    t38 = !isCollapsed && <span>Keluar</span>;
    $[89] = isCollapsed;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== isCollapsed) {
    t39 = isCollapsed && <div className="absolute left-full ml-2.5 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap z-50 pointer-events-none">Keluar<div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" /></div>;
    $[91] = isCollapsed;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== onLogout || $[94] !== t34 || $[95] !== t36 || $[96] !== t38 || $[97] !== t39) {
    t40 = <button onClick={onLogout} title={t34} className={t36}>{t37}{t38}{t39}</button>;
    $[93] = onLogout;
    $[94] = t34;
    $[95] = t36;
    $[96] = t38;
    $[97] = t39;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t33 || $[100] !== t40) {
    t41 = <div className={t33}>{t40}</div>;
    $[99] = t33;
    $[100] = t40;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] !== t31 || $[103] !== t41) {
    t42 = <div className="border-t border-[rgba(0,0,0,0.07)] mt-auto">{t31}{t41}</div>;
    $[102] = t31;
    $[103] = t41;
    $[104] = t42;
  } else {
    t42 = $[104];
  }
  let t43;
  if ($[105] !== t16 || $[106] !== t27 || $[107] !== t28 || $[108] !== t30 || $[109] !== t42) {
    t43 = <aside className={t16}>{t27}{t28}{t30}{t42}</aside>;
    $[105] = t16;
    $[106] = t27;
    $[107] = t28;
    $[108] = t30;
    $[109] = t42;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== t14 || $[112] !== t43) {
    t44 = <>{t14}{t43}</>;
    $[111] = t14;
    $[112] = t43;
    $[113] = t44;
  } else {
    t44 = $[113];
  }
  return t44;
}

/** Item nav yang selalu tampil sebagai tab utama di bottom nav (urut kiri→kanan). */
function _temp4(a, b) {
  return b.href.length - a.href.length;
}
function _temp3(section) {
  return section.items;
}
function _temp2(s_1) {
  return s_1.items.length > 0;
}
function _temp(prev_0) {
  const next_0 = !prev_0;
  localStorage.setItem("sidebar-collapsed", String(next_0));
  return next_0;
}
const PRIMARY_TAB_IDS = ["dashboard", "my-classes", "explore", "tryout"];

/**
 * Ambil maksimal 4 item untuk tab utama. Kalau id yang diminta tidak cukup
 * ditemukan (mis. daftar id siswa dipakai di panel admin), sisanya diisi dari
 * urutan item pertama supaya barnya tidak pernah tampil dengan 2–3 sel raksasa.
 */
function pickPrimaryItems(allItems: SidebarNavItem[], ids: string[]): SidebarNavItem[] {
  const picked = ids.map(id => allItems.find(i => i.id === id)).filter((i): i is SidebarNavItem => Boolean(i));
  if (picked.length >= 4) return picked.slice(0, 4);
  const seen = new Set(picked.map(i => i.href));
  for (const item of allItems) {
    if (picked.length >= 4) break;
    if (!seen.has(item.href) && !item.external) {
      picked.push(item);
      seen.add(item.href);
    }
  }
  return picked;
}

/** Ikon + label untuk satu tab/sheet item — dipakai ulang di tab bar & sheet. */
function BottomNavIcon(t0) {
  const $ = _c(9);
  const {
    item,
    isActive
  } = t0;
  const Icon = item.icon;
  let t1;
  if ($[0] !== Icon || $[1] !== isActive || $[2] !== item.lottieSrc) {
    t1 = item.lottieSrc ? <LottiePlayer src={item.lottieSrc} loop={isActive} autoplay={isActive} hoverPlay={!isActive} className="h-full w-full" /> : <Icon className={cn("h-5 w-5 transition-colors duration-150", isActive ? "text-[var(--brand-primary)]" : "text-gray-400 group-hover:text-gray-700")} />;
    $[0] = Icon;
    $[1] = isActive;
    $[2] = item.lottieSrc;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== item.badge) {
    t2 = item.badge !== undefined && <span className="absolute -top-2 -right-2.5 min-w-[15px] rounded-full bg-[var(--brand-primary)] px-1 py-0.5 text-center text-[8px] font-bold leading-none text-[var(--brand-on-primary,#fff)]">{item.badge}</span>;
    $[4] = item.badge;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <span className="relative h-5 w-5 shrink-0">{t1}{t2}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function MobileBottomNav(t0) {
  const $ = _c(43);
  const {
    sections,
    activeHref,
    brandName,
    onLogout,
    primaryTabIds: t1
  } = t0;
  const primaryTabIds = t1 === undefined ? PRIMARY_TAB_IDS : t1;
  const [moreOpen, setMoreOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      onEscape: () => setMoreOpen(false),
      lockScroll: true
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const moreSheetRef = useFocusTrap(moreOpen, t2);
  let t3;
  if ($[1] !== sections) {
    t3 = sections.flatMap(_temp5);
    $[1] = sections;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const allItems = t3;
  let t4;
  if ($[3] !== allItems || $[4] !== primaryTabIds) {
    t4 = pickPrimaryItems(allItems, primaryTabIds);
    $[3] = allItems;
    $[4] = primaryTabIds;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const primaryItems = t4;
  let t5;
  if ($[6] !== primaryItems) {
    t5 = new Set(primaryItems.map(_temp6));
    $[6] = primaryItems;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const primaryHrefs = t5;
  const [moreSearch, setMoreSearch] = useState("");
  let t6;
  if ($[8] !== moreSearch || $[9] !== primaryHrefs || $[10] !== sections) {
    const q = moreSearch.trim().toLowerCase();
    t6 = sections.map(s => ({
      ...s,
      items: s.items.filter(i_0 => !primaryHrefs.has(i_0.href) && (!q || i_0.label.toLowerCase().includes(q)))
    })).filter(_temp7);
    $[8] = moreSearch;
    $[9] = primaryHrefs;
    $[10] = sections;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const moreSections = t6;
  const activeInMore = Boolean(activeHref) && !primaryHrefs.has(activeHref);
  let t7;
  if ($[12] !== moreSections) {
    t7 = moreSections.some(_temp9);
    $[12] = moreSections;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const moreHasBadge = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMoreOpen(false);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== activeHref) {
    t9 = [activeHref];
    $[15] = activeHref;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  useEffect(t8, t9);
  const t10 = `${brandName} mobile navigation`;
  let t11;
  if ($[17] !== activeHref || $[18] !== primaryItems) {
    let t12;
    if ($[20] !== activeHref) {
      t12 = item => {
        const isActive = item.href === activeHref;
        return <NavLink key={item.id} item={item} data-tour={`nav-item-${item.id}`} aria-current={isActive ? "page" : undefined} className={cn("group relative flex min-w-0 flex-auto min-h-[52px] flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1.5 text-[11px] font-semibold leading-tight transition-all duration-200 active:scale-[0.96]", isActive ? "bg-[var(--brand-primary)]/8 text-[var(--brand-primary)]" : "text-gray-500 hover:bg-black/[0.04] hover:text-gray-900")}><BottomNavIcon item={item} isActive={isActive} /><span className="max-w-full truncate">{item.label}</span></NavLink>;
      };
      $[20] = activeHref;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t11 = primaryItems.map(t12);
    $[17] = activeHref;
    $[18] = primaryItems;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[22] !== activeInMore || $[23] !== moreHasBadge || $[24] !== moreOpen || $[25] !== moreSections.length) {
    t12 = moreSections.length > 0 && <button type="button" onClick={() => {
      setMoreSearch("");
      setMoreOpen(true);
    }} aria-haspopup="dialog" aria-expanded={moreOpen} aria-label="Menu lainnya" className={cn("group relative flex min-w-0 flex-auto min-h-[52px] flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1.5 text-[11px] font-semibold leading-tight transition-all duration-200 active:scale-[0.96]", activeInMore ? "bg-[var(--brand-primary)]/8 text-[var(--brand-primary)]" : "text-gray-500 hover:bg-black/[0.04] hover:text-gray-900")}><span className="relative h-5 w-5 shrink-0"><MoreHorizontal className={cn("h-5 w-5 transition-colors duration-150", activeInMore ? "text-[var(--brand-primary)]" : "text-gray-400 group-hover:text-gray-700")} />{moreHasBadge && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[var(--brand-primary)]" aria-hidden={true} />}</span><span>Lainnya</span></button>;
    $[22] = activeInMore;
    $[23] = moreHasBadge;
    $[24] = moreOpen;
    $[25] = moreSections.length;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <div className="flex items-stretch gap-1">{t11}{t12}</div>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t13) {
    t14 = <nav className="fixed inset-x-0 bottom-0 z-50 bg-white px-2 pt-2 pb-[calc(0.6rem+env(safe-area-inset-bottom))] shadow-[0_-1px_0_rgba(0,0,0,0.07)] md:hidden" aria-label={t10}>{t13}</nav>;
    $[30] = t10;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== activeHref || $[34] !== moreOpen || $[35] !== moreSearch || $[36] !== moreSections || $[37] !== moreSheetRef || $[38] !== onLogout) {
    t15 = moreOpen && <div className="fixed inset-0 z-[var(--z-overlay)] md:hidden" role="dialog" aria-modal="true" aria-label="Menu lainnya"><button type="button" aria-label="Tutup menu" onClick={() => setMoreOpen(false)} className="absolute inset-0 bg-black/40 animate-fade-in" /><div ref={moreSheetRef} className="absolute inset-x-0 bottom-0 max-h-[80dvh] overflow-y-auto rounded-t-2xl bg-white px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.18)] animate-fade-in-up"><div className="mx-auto mb-3 h-1 w-9 rounded-full bg-gray-200" aria-hidden={true} /><div className="mb-2 flex items-center justify-between"><span className="text-[13px] font-bold text-[#09090B]">Menu</span><button type="button" onClick={() => setMoreOpen(false)} aria-label="Tutup" className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-black/[0.05] hover:text-gray-700"><X className="h-4 w-4" /></button></div><div className="relative mb-3"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" /><label htmlFor="more-menu-search" className="sr-only">Cari menu</label><input id="more-menu-search" type="search" value={moreSearch} onChange={e => setMoreSearch(e.target.value)} placeholder="Cari menu..." className="h-11 w-full rounded-xl border border-[rgba(0,0,0,0.08)] bg-[#FAFAFA] pl-9 pr-3 text-[13px] text-[#09090B] placeholder:text-gray-400 focus:border-[var(--brand-primary)]/40 focus:bg-white focus:outline-none" /></div><div className="space-y-4">{moreSections.length === 0 && <p className="py-8 text-center text-[13px] text-gray-400">Tidak ada menu yang cocok.</p>}{moreSections.map((section_0, si) => <div key={section_0.label ?? si}>{section_0.label && <p className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">{section_0.label}</p>}<ul className="grid grid-cols-3 gap-1.5">{section_0.items.map(item_0 => {
                const isActive_0 = item_0.href === activeHref;
                return <li key={item_0.id}><NavLink item={item_0} data-tour={`nav-item-${item_0.id}`} aria-current={isActive_0 ? "page" : undefined} onClick={() => setMoreOpen(false)} className={cn("group flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-xl px-1 py-2 text-center text-[11px] font-semibold leading-tight transition-colors duration-150", isActive_0 ? "bg-[var(--brand-primary)]/8 text-[var(--brand-primary)]" : "text-gray-600 hover:bg-black/[0.04] hover:text-gray-900")}><BottomNavIcon item={item_0} isActive={isActive_0} /><span className="max-w-full truncate">{item_0.label}</span></NavLink></li>;
              })}</ul></div>)}</div>{onLogout && <button onClick={() => {
          setMoreOpen(false);
          onLogout();
        }} className="mt-4 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 text-[13px] font-semibold text-red-500 transition-colors duration-150 hover:bg-red-100 hover:text-red-600"><LogOut className="h-4 w-4" />Keluar</button>}</div></div>;
    $[33] = activeHref;
    $[34] = moreOpen;
    $[35] = moreSearch;
    $[36] = moreSections;
    $[37] = moreSheetRef;
    $[38] = onLogout;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t14 || $[41] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
function _temp9(s_1) {
  return s_1.items.some(_temp8);
}
function _temp8(i_1) {
  return i_1.badge !== undefined;
}
function _temp7(s_0) {
  return s_0.items.length > 0;
}
function _temp6(i) {
  return i.href;
}
function _temp5(section) {
  return section.items;
}